import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { v5 as uuidv5 } from "uuid";

import { useAgentExecutionClient } from "src/api/agents/execution";
import type { ToolCallWithResult } from "src/base-components/agents/ToolCallDisplay";
import { useCaseCopilotInvoke } from "src/cases/copilot/useCaseCopilotInvoke";
import { caseHintsKeys } from "src/cases/queries/hints";
import type {
  SessionMessagesResponse,
  SessionMessagesResponseMessagesInner,
  ToolCallContentBlock,
  ToolMessage,
} from "src/clients/agent-management";
import { toastActions } from "src/design-system/Toast/utils";
import type { DashboardPageParams } from "src/router/urls";
import { formatTerminationReason } from "src/utils/agents/formatTerminationReason";
import { useParamsDecode } from "src/utils/useParamsDecode";

export type CopilotChatMessage =
  | { id: string; role: "user"; content: string }
  | { id: string; role: "assistant"; content: string }
  | {
      id: string;
      role: "tool-group";
      toolCallsWithResults: ToolCallWithResult[];
    };

/** Tags injected as system metadata that should be hidden from the chat UI. */
const HIDDEN_MESSAGE_TAGS = ["files", "eager_investigation_message"];

const isHiddenSystemMessage = (text: string): boolean =>
  HIDDEN_MESSAGE_TAGS.some((tag) => text.startsWith(`<${tag}>`));

const extractMessages = (
  sessionMessages: SessionMessagesResponseMessagesInner[],
): CopilotChatMessage[] => {
  // Build lookup: tool_call_id → ToolMessage
  const toolResultMap = new Map<string, ToolMessage>();
  for (const msg of sessionMessages) {
    if (msg.type === "tool") {
      toolResultMap.set(msg.tool_call_id, msg);
    }
  }

  const result: CopilotChatMessage[] = [];

  for (const msg of sessionMessages) {
    if (msg.type === "human") {
      const text =
        msg.invocation_message ??
        (typeof msg.content === "string" ? msg.content : "");
      if (text && !isHiddenSystemMessage(text)) {
        result.push({ id: msg.id ?? text, role: "user", content: text });
      }
    } else if (msg.type === "ai") {
      const blocks = msg.content_blocks ?? [];

      const textContent = blocks
        .filter((b) => b.type === "text")
        .map((b) => b.text)
        .filter((text): text is string => text !== undefined && text !== null)
        .join("\n\n");

      const toolCalls = blocks.filter(
        (b) => b.type === "tool_call",
      ) as ToolCallContentBlock[];

      const aiId = msg.id ?? "";

      if (toolCalls.length > 0) {
        result.push({
          id: `${aiId}-tools`,
          role: "tool-group",
          toolCallsWithResults: toolCalls.map((tc) => ({
            toolCall: tc,
            toolResult: toolResultMap.get(tc.id) ?? null,
          })),
        });
      }
      if (textContent) {
        result.push({
          id: `${aiId}-text`,
          role: "assistant",
          content: textContent,
        });
      }
    }
  }

  // Keep BE order: newest first. The component handles rendering order.
  return result;
};

/**
 * Checks whether any newly added messages (since `lastKnownId`) are tool
 * messages.  Messages are expected in **newest-first** order.
 */
export const hasNewToolMessages = (
  messages: SessionMessagesResponseMessagesInner[],
  lastKnownId: string | null | undefined,
): boolean => {
  const boundaryIndex = lastKnownId
    ? messages.findIndex((m) => m.id === lastKnownId)
    : -1;
  const newMessages =
    boundaryIndex === -1 ? messages : messages.slice(0, boundaryIndex);
  return newMessages.some((m) => m.type === "tool");
};

export const TERMINAL_STATUSES = new Set(["success", "failed", "cancelled"]);

export const SESSION_CACHE_KEY_PREFIX = "agent-execution";

export const sessionKeys = {
  messages: (sessionId: string | null) =>
    [SESSION_CACHE_KEY_PREFIX, "sessions", sessionId, "messages"] as const,
  /** Eager-loading signal, keyed by (userId, caseId) for version independence. */
  eagerLoading: (userId: string | undefined, caseId: string) =>
    [SESSION_CACHE_KEY_PREFIX, "eager-loading", userId, caseId] as const,
};

const SESSION_NAMESPACE = "a3f2b8c1-7d4e-4f9a-b6e5-1c8d9e0f2a3b";

const PAGE_SIZE = 50;

/** Shared fetch for session messages. 404 → empty (never throws). */
export const fetchSessionMessages = async ({
  client,
  sessionId,
  pageParam,
}: {
  client: NonNullable<ReturnType<typeof useAgentExecutionClient>["client"]>;
  sessionId: string;
  pageParam?: string;
}): Promise<SessionMessagesResponse> => {
  try {
    const response =
      await client.listSessionMessagesCopilotsManagementApiV0SessionsSessionIdMessagesGet(
        {
          sessionId,
          pageSize: PAGE_SIZE,
          cursor: pageParam ?? undefined,
        },
      );
    return response.data;
  } catch (error) {
    // BE returns 404 when no session exists yet — treat as empty conversation.
    // Always return empty (never throw) so that polling continues and
    // optimistic updates from invoke are not overwritten by an error state.
    if (!pageParam && isAxiosError(error) && error.response?.status === 404) {
      return {
        session: undefined,
        messages: [],
      } as unknown as SessionMessagesResponse;
    }
    throw error;
  }
};

/** Shared infinite query options + queryFn so both hooks use identical fetch logic. */
export const copilotMessagesQueryBase = ({
  sessionId,
  client,
  queryClient,
}: {
  sessionId: string | null;
  client: ReturnType<typeof useAgentExecutionClient>["client"];
  queryClient: QueryClient;
}) => {
  const queryKey = sessionKeys.messages(sessionId);
  return {
    queryKey,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: SessionMessagesResponse) =>
      lastPage.next_cursor ?? undefined,
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      if (!client) throw new Error("Agent execution client not available");
      if (!sessionId) throw new Error("Session ID not available");

      const data = await fetchSessionMessages({ client, sessionId, pageParam });
      if (!pageParam) {
        return preserveCachedMessages(data, queryClient, queryKey);
      }
      return data;
    },
  };
};

/**
 * Keep the cached first page when the server returns empty (404 / not started)
 * or while an optimistic message (no `id`) hasn't been echoed back yet.
 */
export const preserveCachedMessages = (
  data: SessionMessagesResponse,
  queryClient: QueryClient,
  queryKey: readonly unknown[],
): SessionMessagesResponse => {
  const serverMessages = data.messages ?? [];
  const cached =
    queryClient.getQueryData<InfiniteData<SessionMessagesResponse>>(queryKey);
  const cachedMessages = cached?.pages?.[0]?.messages ?? [];

  // Case 1: server returned empty (e.g. 404) — keep existing data.
  if (serverMessages.length === 0 && cachedMessages.length > 0) {
    return cached!.pages[0];
  }

  // Case 2: optimistic message pending (first cached message has no id).
  const hasOptimisticMessage =
    cachedMessages.length > 0 && !cachedMessages[0]?.id;
  if (hasOptimisticMessage && cachedMessages.length > serverMessages.length) {
    return cached!.pages[0];
  }

  return data;
};

export const getSessionId = (
  userId: string | undefined,
  caseId: string,
  agentVersionId: string | undefined,
): string | null =>
  userId
    ? uuidv5(
        `${caseId}:${userId}:${agentVersionId ?? "latest"}`,
        SESSION_NAMESPACE,
      )
    : null;

type UseCaseCopilotSessionParams = {
  agentId: string | undefined;
  agentVersionId: string | undefined;
  caseId: string;
  userId: string | undefined;
  environment: string;
  fileIds: string[];
};

export const useCaseCopilotSession = ({
  agentId,
  agentVersionId,
  caseId,
  userId,
  environment,
  fileIds,
}: UseCaseCopilotSessionParams) => {
  const sessionId = getSessionId(userId, caseId, agentVersionId);

  const [hasStarted, setHasStarted] = useState(false);

  // Keeps polling alive after invoke until a non-terminal status appears.
  const awaitingSessionRef = useRef(false);
  const awaitingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setHasStarted(false);
    awaitingSessionRef.current = false;
    clearTimeout(awaitingTimeoutRef.current);
  }, [sessionId]);

  const { client } = useAgentExecutionClient();
  const queryClient = useQueryClient();
  const { wsId } = useParamsDecode<DashboardPageParams>();

  const messagesQueryKey = useMemo(
    () => sessionKeys.messages(sessionId),
    [sessionId],
  );
  const queryBase = copilotMessagesQueryBase({
    sessionId,
    client,
    queryClient,
  });

  const invokeMutation = useCaseCopilotInvoke({
    agentId,
    agentVersionId,
    caseId,
    sessionId,
    environment,
    fileIds,
    messagesQueryKey,
    onInvokeStarted: () => {
      setHasStarted(true);
      awaitingSessionRef.current = true;
      clearTimeout(awaitingTimeoutRef.current);
      awaitingTimeoutRef.current = setTimeout(() => {
        awaitingSessionRef.current = false;
      }, 5000);
    },
  });

  const lastKnownMessageIdRef = useRef<string | null | undefined>(undefined);

  const messagesQuery = useInfiniteQuery({
    ...queryBase,
    enabled: !!client && !!sessionId,
    refetchInterval: (query) => {
      if (query.state.status === "error") return false;
      if (invokeMutation.isPending) return false;

      const sessionStatus = query.state.data?.pages?.[0]?.session?.status;

      if (sessionStatus && !TERMINAL_STATUSES.has(sessionStatus)) {
        awaitingSessionRef.current = false;
        clearTimeout(awaitingTimeoutRef.current);
        return 1000;
      }

      return awaitingSessionRef.current ? 1000 : false;
    },
    select: (data) => ({
      session: data.pages[0]?.session,
      messages: extractMessages(data.pages.flatMap((p) => p.messages ?? [])),
    }),
  });

  // Invalidate case hints when new tool messages arrive.
  useEffect(() => {
    const cached =
      queryClient.getQueryData<InfiniteData<SessionMessagesResponse>>(
        messagesQueryKey,
      );
    const rawMessages = cached?.pages?.[0]?.messages ?? [];
    const newestId = rawMessages[0]?.id ?? null;

    if (lastKnownMessageIdRef.current === undefined) {
      lastKnownMessageIdRef.current = newestId;
      return;
    }
    if (newestId === lastKnownMessageIdRef.current) return;

    if (hasNewToolMessages(rawMessages, lastKnownMessageIdRef.current)) {
      queryClient.invalidateQueries({
        queryKey: caseHintsKeys.get(wsId, caseId),
      });
    }
    lastKnownMessageIdRef.current = newestId;
  }, [
    messagesQuery.dataUpdatedAt,
    queryClient,
    messagesQueryKey,
    wsId,
    caseId,
  ]);

  const session = messagesQuery.data?.session;
  const sessionStatus = session?.status;
  const isSessionTerminal =
    sessionStatus != null && TERMINAL_STATUSES.has(sessionStatus);

  // Eager-loading signal from useCopilotSessionStatus.
  const { data: isEagerLoading = false } = useQuery<boolean>({
    queryKey: sessionKeys.eagerLoading(userId, caseId),
    queryFn: () => false,
    enabled: false,
  });

  const messages = messagesQuery.data?.messages ?? [];

  const sendMessage = useCallback(
    (text: string) => {
      if (invokeMutation.isPending) return;
      if (!agentId) {
        toastActions.failure({
          title: "Copilot not available",
          description: "No copilot agent is configured for this case template.",
        });
        return;
      }

      invokeMutation.mutate(text);
    },
    [agentId, invokeMutation],
  );

  const isActive =
    invokeMutation.isPending ||
    awaitingSessionRef.current ||
    (isEagerLoading && (!hasStarted || !isSessionTerminal)) ||
    ((hasStarted || sessionStatus != null) && !isSessionTerminal);

  const failureMessage =
    sessionStatus === "failed"
      ? session?.termination_reason
        ? `Something went wrong: ${formatTerminationReason(session.termination_reason)}`
        : "Something went wrong"
      : null;

  return {
    messages,
    isLoading: messagesQuery.isLoading,
    isActive,
    failureMessage,
    sendMessage,
    fetchNextPage: messagesQuery.fetchNextPage,
    hasNextPage: messagesQuery.hasNextPage,
    isFetchingNextPage: messagesQuery.isFetchingNextPage,
  };
};
